import React, { useEffect, useState } from "react";
import missingone from "../../assets/images/missingone.png";
import arrowLeft from "../../assets/images/arrow-leftttttt.svg";
import { useNavigate, useParams } from "react-router";
import { emergencyService } from "services/emergency.service";
import useCurrentUser from "hooks/useCurrentUser";
import { toast } from "react-toastify";
import { paymnetService } from "services/payment.service";

const MissingPersonDetails = () => {
  const [data, setData] = useState(null);
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const { id } = useParams();

  const handleBack = () => {
    navigate(-1);
  };

  const getEmergencyDetails = async () => {
    try {
      const response = await emergencyService.emergencyListDetails(id);
      setData(response?.data?.data || null);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to fetch details");
    }
  };

  useEffect(() => {
    if (id) {
      getEmergencyDetails();
    }
  }, [id]);

  const handlePayNow = async (emergency) => {
    try {
      const response = await paymnetService.createCheckoutSession({
        type: "Emergency",
        id: emergency?._id,
      });
      const url = response?.data?.data?.url;
      if (url) {
        window.open(url, "_blank");
      }
    } catch (err) {
      toast.error(err?.data?.message || "Payment failed");
    }
  };

  // Location (adjust field names to your API if needed)
  const latitude =
    data?.coordinates?.[1] ??
    null;

  const longitude =
    data?.coordinates?.[0] ??
    null;

  const hasLocation = latitude != null && longitude != null;

  return (
    <div className="missing-details-page">
      <div className="container-box">
        {/* Header */}
        <div className="top-head d-flex align-items-center text-white">
          <h2 className="d-flex align-items-center mb-0">
            <img
              src={arrowLeft}
              className="me-2 cursor-pointer"
              onClick={handleBack}
              alt="Back"
            />
            <span>Missing Person</span>
          </h2>
        </div>

        <div className="outer-white pt-3">
          {!data ? (
            <div className="text-center py-5">Loading details...</div>
          ) : (
            <div className="bg-card-gray rounded-3 p-3 p-md-4">
              {/* Section 1: Person Info */}
              <div className="row g-3 align-items-start mb-3">
                <div className="col-12 col-md-4 text-center text-md-start">
                  <div className="mb-3">
                    {data?.missingPerson?.avatar ? (
                      <img
                        src={`${user?.baseS3Url}${data?.missingPerson?.avatar}`}
                        alt="Missing Person"
                        className="img-fluid rounded-3"
                        style={{ maxHeight: "260px", objectFit: "cover" }}
                      />
                    ) : (
                      <img
                        src={missingone}
                        alt="Missing Person"
                        className="img-fluid rounded-3"
                        style={{ maxHeight: "260px", objectFit: "cover" }}
                      />
                    )}
                  </div>

                  <h5 className="mb-1">
                    {data?.missingPerson?.fullName || "Unknown Name"}
                  </h5>
                  <div className="small text-muted mb-2">
                    Case ID: <strong>{data?._id || "-"}</strong>
                  </div>

                  <div className="small d-flex flex-column gap-1">
                    <div>
                      <span className="text-muted">Age: </span>
                      <strong>{data?.missingPerson?.age || "-"}</strong>
                    </div>
                    <div>
                      <span className="text-muted">Gender: </span>
                      <strong>{data?.missingPerson?.gender || "-"}</strong>
                    </div>
                    <div>
                      <span className="text-muted">Requester Email: </span>
                      <strong>{data?.requesterEmail || "-"}</strong>
                    </div>
                    {data?.createdAt && (
                      <div>
                        <span className="text-muted">Reported On: </span>
                        <strong>
                          {new Date(data.createdAt).toLocaleString()}
                        </strong>
                      </div>
                    )}
                    {data?.status && (
                      <div>
                        <span className="text-muted">Case Status: </span>
                        <strong>{data.status}</strong>
                      </div>
                    )}
                    {data?.paymentStatus && (
                      <div>
                        <span className="text-muted">Payment Status: </span>
                        <strong>{data.paymentStatus}</strong>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-8">
                  {/* Section 2: Description */}
                  <div className="mb-3">
                    <h5 className="mb-2">Description</h5>
                    <p className="mb-0">
                      {data?.description || "No description provided."}
                    </p>
                    {data?.rejectedReason && (
                      <div className="mt-3 small">
                        <div className="fw-bold text-danger mb-1">Rejected</div>
                        <div>Reason: {data.rejectedReason}</div>
                      </div>
                    )}
                  </div>

                  {/* Section 3: Location */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                      <div>
                        <h6 className="mb-1">Last Known Location</h6>
                        <div className="small text-muted">
                          Coordinates and map preview
                        </div>
                      </div>
                      {hasLocation && (
                        <div className="small text-end">
                          <div>
                            <span className="text-muted">Lat: </span>
                            <strong>{latitude}</strong>
                          </div>
                          <div>
                            <span className="text-muted">Lng: </span>
                            <strong>{longitude}</strong>
                          </div>
                        </div>
                      )}
                    </div>

                    {data?.locationAddress && (
                      <div className="mb-2 small">
                        <span className="text-muted">Address: </span>
                        <strong>{data.locationAddress}</strong>
                      </div>
                    )}

                    {hasLocation ? (
                      <div className="rounded-3 overflow-hidden border">
                        <iframe
                          title="Last known location"
                          src={`https://www.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`}
                          style={{ width: "100%", height: "260px", border: 0 }}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      </div>
                    ) : (
                      <div className="small text-muted">
                        Location coordinates are not available for this case.
                      </div>
                    )}
                  </div>

                  {/* Section 4: Payment (only requester) */}
                  {data?.requesterId === user?._id && (
                    <div>
                      {data?.paymentStatus === "Paid" ? (
                        <div className="alert alert-success mb-0 py-2 small">
                          Payment Status: <strong>Paid</strong>
                        </div>
                      ) : data?.paymentStatus === "UserDeny" ? (
                        <div className="alert alert-secondary mb-0 py-2 small">
                          Payment Status: <strong>Denied</strong>
                        </div>
                      ) : data?.isChargeRequestedByAdmin === true ? (
                        <button
                          className="btn pay-btn blue-btn"
                          onClick={() => handlePayNow(data)}
                        >
                          Pay Now
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MissingPersonDetails;
